import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    ownerName: '',
    email: '',
    password: '',
    confirmPassword: '',
    businessType: '',
    industry: '',
    transactionVolume: '',
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  const businessTypes = [
    { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'llc', label: 'Limited Liability Company (LLC)' },
    { value: 'corporation', label: 'Corporation' },
    { value: 'nonprofit', label: 'Non-Profit Organization' }
  ];

  const industries = [
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'professional_services', label: 'Professional Services' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'technology', label: 'Technology & Software' },
    { value: 'healthcare', label: 'Healthcare & Medical' },
    { value: 'construction', label: 'Construction & Real Estate' },
    { value: 'hospitality', label: 'Hospitality & Food Service' },
    { value: 'consulting', label: 'Consulting & Advisory' },
    { value: 'education', label: 'Education & Training' },
    { value: 'other', label: 'Other' }
  ];

  const transactionVolumes = [
    { value: 'low', label: 'Less than $50K annually' },
    { value: 'medium', label: '$50K - $500K annually' },
    { value: 'high', label: '$500K - $2M annually' },
    { value: 'enterprise', label: 'Over $2M annually' }
  ];

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (/[A-Z]/?.test(password)) strength += 25;
    if (/[0-9]/?.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/?.test(password)) strength += 25;
    return strength;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.businessName?.trim()) {
      newErrors.businessName = 'Business name is required';
    }

    if (!formData?.ownerName?.trim()) {
      newErrors.ownerName = 'Owner name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else {
      // Enhanced email validation
      const email = formData?.email?.trim()?.toLowerCase();
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      
      if (!emailRegex?.test(email)) {
        newErrors.email = 'Please enter a valid email address';
      } else if (email?.length > 254) {
        newErrors.email = 'Email address is too long';
      } else if (email?.includes('..') || email?.startsWith('.') || email?.endsWith('.')) {
        newErrors.email = 'Email address contains invalid characters';
      }
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.businessType) {
      newErrors.businessType = 'Please select your business type';
    }

    if (!formData?.industry) {
      newErrors.industry = 'Please select your industry';
    }

    if (!formData?.transactionVolume) {
      newErrors.transactionVolume = 'Please select your transaction volume';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Update password strength
    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const createOrganizationSlug = (businessName) => {
    return businessName
      ?.toLowerCase()
      ?.replace(/[^a-z0-9\s-]/g, '')
      ?.replace(/\s+/g, '-')
      ?.replace(/-+/g, '-')
      ?.replace(/^-|-$/g, '')
      ?.substring(0, 50) || 'organization';
  };

  // Enhanced email normalization function
  const normalizeEmail = (email) => {
    if (!email) return '';
    
    return email
      ?.trim()
      ?.toLowerCase()
      ?.replace(/\s+/g, '') // Remove any whitespace
      ?.replace(/[^\w@.-]/g, ''); // Remove any invalid characters except @ . - and word characters
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Enhanced email normalization and validation
      const normalizedEmail = normalizeEmail(formData?.email);
      
      // Additional client-side check for normalized email
      if (!normalizedEmail || normalizedEmail?.length < 5) {
        throw new Error('Please enter a valid email address');
      }

      console.log('Attempting registration with email:', normalizedEmail);

      // Step 1: Create user account with Supabase Auth
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: normalizedEmail,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.ownerName?.trim(),
            business_name: formData?.businessName?.trim(),
            business_type: formData?.businessType,
            industry: formData?.industry,
            transaction_volume: formData?.transactionVolume
          },
          emailRedirectTo: `${window.location?.origin}/dashboard`
        }
      });

      if (authError) {
        console.error('Supabase Auth Error:', authError);
        throw authError;
      }

      if (!authData?.user) {
        throw new Error('Failed to create user account - no user returned');
      }

      console.log('User created successfully:', authData?.user?.id);

      // Step 2: Create organization for the new user
      const organizationSlug = createOrganizationSlug(formData?.businessName);
      const { data: orgData, error: orgError } = await supabase?.from('organizations')?.insert({
          name: formData?.businessName?.trim(),
          slug: organizationSlug,
          owner_id: authData?.user?.id,
          description: `${formData?.industry} organization`,
          status: 'active',
          settings: {
            business_type: formData?.businessType,
            industry: formData?.industry,
            transaction_volume: formData?.transactionVolume
          }
        })?.select()?.single();

      if (orgError) {
        console.error('Organization creation error:', orgError);
        // Don't throw error here as user account is already created
        // The user can create organization later in the settings
      }

      // Step 3: Add user to organization members if organization was created successfully
      if (orgData && !orgError) {
        const { error: memberError } = await supabase?.from('organization_members')?.insert({
            organization_id: orgData?.id,
            user_id: authData?.user?.id,
            role: 'org_admin',
            joined_at: new Date()?.toISOString()
          });

        if (memberError) {
          console.error('Organization membership error:', memberError);
          // Don't throw error here as user and organization are created
        }
      }

      console.log('Registration successful:', {
        userId: authData?.user?.id,
        email: authData?.user?.email,
        organizationId: orgData?.id || null
      });

      // Show success message instead of immediately redirecting
      setErrors({
        success: 'Account created successfully! Please check your email for verification instructions.'
      });
      
    } catch (error) {
      console.error('Registration failed:', error);
      
      let errorMessage = 'Registration failed. Please try again.';
      
      // Enhanced error handling for specific Supabase auth errors
      if (error?.message?.includes('User already registered')) {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (error?.message?.includes('Password should be at least 6 characters')) {
        errorMessage = 'Password must be at least 6 characters long.';
      } else if (error?.message?.includes('Unable to validate email address') || error?.message?.includes('invalid email')) {
        errorMessage = `Please enter a valid email address. If you're using a valid email and still seeing this error, try using a different email provider (Gmail, Outlook, etc.).`;
      } else if (error?.message?.includes('Email address') && error?.message?.includes('invalid')) {
        errorMessage = `The email address format is not accepted. Please try:
        • Using a different email provider
        • Checking for typos in your email
        • Using a business email address instead of personal`;
      } else if (error?.message?.includes('Email rate limit exceeded')) {
        errorMessage = 'Too many registration attempts. Please wait a few minutes and try again.';
      } else if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        errorMessage = 'Network connection error. Please check your internet connection and try again.';
      } else if (error?.message?.includes('Invalid login credentials')) {
        errorMessage = 'There was an issue with your account credentials. Please try again.';
      } else if (error?.message?.includes('signup disabled')) {
        errorMessage = 'New user registration is currently disabled. Please contact support.';
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      
      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-error';
    if (passwordStrength < 50) return 'bg-warning';
    if (passwordStrength < 75) return 'bg-accent';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Weak';
    if (passwordStrength < 50) return 'Fair';
    if (passwordStrength < 75) return 'Good';
    return 'Strong';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Business Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Business Information</h3>
        
        <Input
          label="Business Name"
          type="text"
          placeholder="Enter your business name"
          value={formData?.businessName}
          onChange={(e) => handleInputChange('businessName', e?.target?.value)}
          error={errors?.businessName}
          required
        />

        <Input
          label="Owner/Contact Name"
          type="text"
          placeholder="Enter your full name"
          value={formData?.ownerName}
          onChange={(e) => handleInputChange('ownerName', e?.target?.value)}
          error={errors?.ownerName}
          required
        />

        <Select
          label="Business Type"
          placeholder="Select your business type"
          options={businessTypes}
          value={formData?.businessType}
          onChange={(value) => handleInputChange('businessType', value)}
          error={errors?.businessType}
          required
        />

        <Select
          label="Industry"
          placeholder="Select your industry"
          options={industries}
          value={formData?.industry}
          onChange={(value) => handleInputChange('industry', value)}
          error={errors?.industry}
          searchable
          required
        />

        <Select
          label="Estimated Annual Transaction Volume"
          placeholder="Select transaction volume"
          options={transactionVolumes}
          value={formData?.transactionVolume}
          onChange={(value) => handleInputChange('transactionVolume', value)}
          error={errors?.transactionVolume}
          description="This helps us customize AI recommendations for your business"
          required
        />
      </div>
      {/* Account Credentials */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Account Credentials</h3>
        
        <Input
          label="Email Address"
          type="email"
          placeholder="Enter your business email"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          description="This will be your login email"
          required
        />

        <div className="space-y-2">
          <Input
            label="Password"
            type="password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
          />
          
          {formData?.password && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Password strength:</span>
                <span className={`font-medium ${
                  passwordStrength < 50 ? 'text-error' : 
                  passwordStrength < 75 ? 'text-warning' : 'text-success'
                }`}>
                  {getPasswordStrengthText()}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                  style={{ width: `${passwordStrength}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <Input
          label="Confirm Password"
          type="password"
          placeholder="Confirm your password"
          value={formData?.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
        />
      </div>
      {/* Terms and Conditions */}
      <div className="space-y-4">
        <Checkbox
          label="I agree to the Terms of Service and Privacy Policy"
          checked={formData?.agreeToTerms}
          onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
          error={errors?.agreeToTerms}
          required
        />
      </div>
      {/* Email Confirmation Notice */}
      <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Mail" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium">Email Verification Required</p>
            <p className="text-muted-foreground mt-1">
              After creating your account, please check your email for a verification link. You'll need to verify your email address before you can sign in.
            </p>
            <p className="text-muted-foreground mt-2 text-xs">
              <strong>Note:</strong> If you're having trouble with email validation, try using a different email provider (Gmail, Outlook, Yahoo, etc.) or contact support for assistance.
            </p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {errors?.success && (
        <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <p className="text-sm text-success font-medium">{errors?.success}</p>
          </div>
        </div>
      )}

      {/* Submit Error */}
      {errors?.submit && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
            <div className="text-sm">
              <p className="text-error font-medium">Registration Failed</p>
              <p className="text-error mt-1 whitespace-pre-line">{errors?.submit}</p>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={isLoading || errors?.success}
        iconName="UserPlus"
        iconPosition="left"
      >
        {isLoading ? 'Creating Account...' : 'Create Account'}
      </Button>
      {/* Sign In Link */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-primary hover:text-primary/80 font-medium transition-colors"
          >
            Sign In
          </button>
        </p>
      </div>
    </form>
  );
};

export default RegistrationForm;